import React from 'react';
import TodoItem from './TodoItem';

// ═══════════════════════════════════════════════════════════════
// RECURSIVE NODE COMPONENT (for Master View)
// ═══════════════════════════════════════════════════════════════

const TodoNode = ({
  node,
  statuses,
  allTags,
  allTasks = [],
  lockStates = {},
  onToggleComplete,
  onOpenDetails,
  onOpenAttachments,
  onStatusChange,
  onPriorityChange,
  onDifficultyChange,
  onDueDateChange,
  onRenameTask,
  onDeleteTask,
  onArchiveTask,
  onUnarchiveTask,
  onDependencyChange,
  onConditionsChange,
  onFocusTask,
  onAddSubtask,
  onDrillIn,
  onIconChange,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  // Selection for Fokus mode
  selectedTaskIds = new Set(),
  onToggleSelect,
  // Element visibility
  elementVisibility = {},
  // View navigation (for "Open in view" dropdown)
  currentView = 'todolist',
  customViews = [],
  activeCustomViewId = null,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Current campaign ID (for detecting cross-campaign navigation)
  currentCampaignId = null,
  // Decision handlers
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  // For adding missions to nested sprints
  onAddMissionToSprint,
  // Custom fields
  customFields = [],
  onCustomFieldValueChange,
  // Deliverables warning modal
  onSubmitDeliverable,
  onNavigateToParentMission,
  nestedSprints = [],
  allSprints = [],
}) => {
  // Category-based checking: any "completed" category = checked
  const taskStatus = statuses.find(s => s.id === node.task.status);
  const isChecked = node.isSubtask 
    ? node.task.completed === true
    : taskStatus?.category === 'completed';

  const hasChildren = node.children && node.children.length > 0;
  
  // Get blocking dependencies for this task (include all blocking reasons)
  const lockState = lockStates[node.task.id];
  const blockingDeps = lockState?.blockingTasks || [];
  const isLocked = lockState?.isLocked && node.task.status !== 'completed';
  const isExcluded = lockState?.isExcluded;

  return (
    <div className={`todo-node ${hasChildren ? 'todo-node--has-children' : ''}`}>
      <TodoItem
        task={node.task}
        depth={node.depth}
        isSubtask={node.isSubtask}
        isParent={node.isParent}
        nestedSprint={node.nestedSprint}
        blockingDependencies={blockingDeps}
        parentMissionName={lockState?.parentMissionName}
        parentCampaignName={lockState?.parentCampaignName}
        parentMissionId={lockState?.parentMissionId}
        parentSprintId={lockState?.parentSprintId}
        isLocked={isLocked}
        isExcluded={isExcluded}
        lockReason={lockState?.lockReason}
        statuses={statuses}
        allTags={allTags}
        allTasks={node.sprintTasks || allTasks}
        isChecked={isChecked}
        onToggleComplete={() => onToggleComplete(node.task.id, node.isSubtask, node.parentTask?.id)}
        onOpenDetails={() => !node.isSubtask && onOpenDetails?.(node.task.id)}
        onOpenAttachments={!node.isSubtask ? onOpenAttachments : undefined}
        onStatusChange={!node.isSubtask ? onStatusChange : undefined}
        onPriorityChange={!node.isSubtask ? onPriorityChange : undefined}
        onDifficultyChange={!node.isSubtask ? onDifficultyChange : undefined}
        onDueDateChange={!node.isSubtask ? onDueDateChange : undefined}
        onRename={!node.isSubtask ? onRenameTask : undefined}
        onDelete={!node.isSubtask ? onDeleteTask : undefined}
        onArchive={!node.isSubtask ? onArchiveTask : undefined}
        onUnarchive={!node.isSubtask ? onUnarchiveTask : undefined}
        onDependencyChange={!node.isSubtask ? onDependencyChange : undefined}
        onConditionsChange={!node.isSubtask ? onConditionsChange : undefined}
        onFocusTask={!node.isSubtask ? onFocusTask : undefined}
        onAddSubtask={!node.isSubtask ? onAddSubtask : undefined}
        onDrillIn={node.nestedSprint ? () => onDrillIn(node.nestedSprint) : undefined}
        onIconChange={!node.isSubtask ? onIconChange : undefined}
        onAddStatus={!node.isSubtask ? onAddStatus : undefined}
        onDeleteStatus={!node.isSubtask ? onDeleteStatus : undefined}
        onRenameStatus={!node.isSubtask ? onRenameStatus : undefined}
        onSetStatusAutoSync={!node.isSubtask ? onSetStatusAutoSync : undefined}
        isSelected={!node.isSubtask && selectedTaskIds.has(node.task.id)}
        onToggleSelect={!node.isSubtask ? onToggleSelect : undefined}
        elementVisibility={elementVisibility}
        currentView={currentView}
        customViews={customViews}
        activeCustomViewId={activeCustomViewId}
        sourceCampaignId={node.sprintId || null}
        onViewChange={onViewChange}
        onSelectCustomView={onSelectCustomView}
        onGoToTaskInView={onGoToTaskInView}
        onDecisionAnswerChange={!node.isSubtask ? onDecisionAnswerChange : undefined}
        onDecisionOptionsChange={!node.isSubtask ? onDecisionOptionsChange : undefined}
        customFields={!node.isSubtask ? customFields : []}
        onCustomFieldValueChange={!node.isSubtask ? onCustomFieldValueChange : undefined}
        onAddMissionToSprint={node.nestedSprint && onAddMissionToSprint ? () => onAddMissionToSprint(node.nestedSprint.id) : undefined}
        onSubmitDeliverable={!node.isSubtask ? onSubmitDeliverable : undefined}
        onNavigateToParentMission={onNavigateToParentMission}
        nestedSprints={nestedSprints}
        allSprints={allSprints}
      />
      {hasChildren && (
        <div className="todo-node__children">
          {/* Add New Mission row - sticky at top of each nested list */}
          {node.nestedSprint && onAddMissionToSprint && (
            <button
              className="todo-node__add-row"
              onClick={() => onAddMissionToSprint(node.nestedSprint.id)}
              type="button"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Add new mission to <span className="todo-node__add-row-name">"{node.task.name || 'Untitled'}"</span></span>
            </button>
          )}
          {node.children.map((child, index) => (
            <TodoNode
              key={`${child.task.id}-${child.depth}-${index}`}
              node={child}
              statuses={statuses}
              allTags={allTags}
              allTasks={allTasks}
              lockStates={lockStates}
              onToggleComplete={onToggleComplete}
              onOpenDetails={onOpenDetails}
              onOpenAttachments={onOpenAttachments}
              onStatusChange={onStatusChange}
              onPriorityChange={onPriorityChange}
              onDifficultyChange={onDifficultyChange}
              onDueDateChange={onDueDateChange}
              onRenameTask={onRenameTask}
              onDeleteTask={onDeleteTask}
              onArchiveTask={onArchiveTask}
              onUnarchiveTask={onUnarchiveTask}
              onDependencyChange={onDependencyChange}
              onConditionsChange={onConditionsChange}
              onFocusTask={onFocusTask}
              onAddSubtask={onAddSubtask}
              onDrillIn={onDrillIn}
              onIconChange={onIconChange}
              onAddStatus={onAddStatus}
              onDeleteStatus={onDeleteStatus}
              onRenameStatus={onRenameStatus}
              onSetStatusAutoSync={onSetStatusAutoSync}
              selectedTaskIds={selectedTaskIds}
              onToggleSelect={onToggleSelect}
              elementVisibility={elementVisibility}
              currentView={currentView}
              customViews={customViews}
              activeCustomViewId={activeCustomViewId}
              currentCampaignId={currentCampaignId}
              onViewChange={onViewChange}
              onSelectCustomView={onSelectCustomView}
              onGoToTaskInView={onGoToTaskInView}
              onDecisionAnswerChange={onDecisionAnswerChange}
              onDecisionOptionsChange={onDecisionOptionsChange}
              onAddMissionToSprint={onAddMissionToSprint}
              customFields={customFields}
              onCustomFieldValueChange={onCustomFieldValueChange}
              onSubmitDeliverable={onSubmitDeliverable}
              onNavigateToParentMission={onNavigateToParentMission}
              nestedSprints={nestedSprints}
              allSprints={allSprints}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TodoNode;
